/**
 * 共用顯示格式化 helpers。
 *
 * - `formatPrice`：numeric string → 在地化貨幣
 * - `pickI18n`：i18n 字典挑選顯示字串（依目前 locale，缺則 fallback chain）
 * - `formatDateTime`：ISO timestamp → 在地時間
 *
 * 三者皆吃 `locale` 參數（預設 zh-TW）；呼叫端通常傳入 `useI18n().locale`。
 */
import type { Locale } from './i18n';

export function formatPrice(
  basePrice: string | number,
  currency: string,
  locale: Locale = 'zh-TW',
): string {
  const value = typeof basePrice === 'number' ? basePrice : Number(basePrice);
  if (!Number.isFinite(value)) return `${currency} ${String(basePrice)}`;
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(value);
  } catch {
    return `${currency} ${value.toFixed(0)}`;
  }
}

/**
 * 由 i18n 字典挑顯示字串。
 *
 * 優先序：目前 locale → zh-TW → 字典內任一非空值 → fallback。
 */
export function pickI18n(
  dict: Record<string, string> | null | undefined,
  fallback: string,
  locale: Locale = 'zh-TW',
): string {
  if (!dict) return fallback;
  const exact = dict[locale];
  if (typeof exact === 'string' && exact.length > 0) return exact;
  if (typeof dict['zh-TW'] === 'string' && dict['zh-TW'].length > 0) return dict['zh-TW'];
  const first = Object.values(dict).find((v) => typeof v === 'string' && v.length > 0);
  return first ?? fallback;
}

export function formatDateTime(iso: string, locale: Locale = 'zh-TW'): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  try {
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(d);
  } catch {
    return d.toISOString();
  }
}
